import { useEmailTemplatePermissions } from '@/pages/email-templates/useEmailTemplatePermissions';
import { apiService } from '@/services/api';
import { EmailTemplateVariable } from '@/types';
import { useQuery } from '@tanstack/react-query';
import { toCamelCase } from '../lib/utils';

interface ApiEmailTemplateVariable {
  /** The variable key used in template placeholders (e.g., 'Customer_Name' becomes '{{Customer_Name}}') */
  key: string;
  /** Category for grouping variables */
  category: string;
}

interface EmailTemplateVariablesResponse {
  success: boolean;
  message: string;
  result: {
    variables: ApiEmailTemplateVariable[];
  };
}

export interface UseEmailTemplateVariablesResult {
  variables: EmailTemplateVariable[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch email template variables from the backend API
 */
export const useEmailTemplateVariables =
  (): UseEmailTemplateVariablesResult => {
    const { canRead } = useEmailTemplatePermissions();

    const query = useQuery({
      queryKey: ['emailTemplateVariables'],
      queryFn: async (): Promise<EmailTemplateVariable[]> => {
        try {
          const response = await apiService.get<EmailTemplateVariablesResponse>(
            '/email-templates/variables'
          );

          // Handle different response structures gracefully
          let result: ApiEmailTemplateVariable[] = [];

          if (
            response.data?.result?.variables &&
            Array.isArray(response.data.result.variables)
          ) {
            result = response.data.result.variables;
          } else {
            console.warn('Unexpected API response structure');
          }

          // Transform API response to match our interface with robust error handling
          const transformedVariables: EmailTemplateVariable[] = [];

          for (let index = 0; index < result.length; index++) {
            try {
              const variable = result[index];
              const key = variable.key;
              const category = variable.category;
              // Generate labelKey from the key using toCamelCase
              const labelKey = `emailTemplates.form.variables.${toCamelCase(key)}`;

              transformedVariables.push({
                key,
                labelKey,
                category,
              });
            } catch (variableError) {
              console.warn(
                `Error processing variable at index ${index}:`,
                variableError,
                result[index]
              );
              // Skip this variable and continue
            }
          }

          return transformedVariables;
        } catch (error) {
          console.error('Error in email template variables query:', error);
          return [] as EmailTemplateVariable[];
        }
      },
      enabled: canRead, // Only fetch if user has permission
      staleTime: 5 * 60 * 1000, // 5 minutes - variables don't change often
      gcTime: 30 * 60 * 1000, // 30 minutes cache time
      retry: 2, // Retry failed requests
    });

    // If query is disabled due to permissions, provide fallback
    if (!canRead) {
      return {
        variables: [] as EmailTemplateVariable[],
        isLoading: false,
        isError: false,
        error: null,
        refetch: query.refetch,
      };
    }

    return {
      variables: query.data || [],
      isLoading: query.isLoading,
      isError: query.isError,
      error: query.error,
      refetch: query.refetch,
    };
  };
